// src/pages/.../BirthdaySection.jsx
import React, { useMemo, useState, useEffect } from "react";
import { FaBirthdayCake, FaBell } from "react-icons/fa";
import { toast } from "react-toastify";
import { sendNotification } from "../../../services/notificationServices";
import {
  getUserIdByAdherentId,
  getUpcomingBirthdays, // ✅ AJOUT (ton nouveau service)
} from "../../../services/adherentServices";

const pickUserId = (u) => u?.UserAdherent?.user_id ?? u?.user_id ?? u?.id ?? null;

const BirthdaySection = ({
  birthdayAdherents = [],
  formatDate,
  calculateAge,
  getDefaultPhoto,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [sendingIds, setSendingIds] = useState(new Set());
  const [userIdsCache, setUserIdsCache] = useState({});

  // ✅ NOUVEAU : mode "today" / "next30"
  const [mode, setMode] = useState("today"); // "today" | "next30"
  const [upcomingAdherents, setUpcomingAdherents] = useState([]);
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);

  const todayLabel = useMemo(() => (formatDate ? formatDate() : ""), [formatDate]);
  const hasToken = !!localStorage.getItem("token");

  // ✅ fetch des anniversaires à venir (à la demande)
  useEffect(() => {
    if (mode !== "next30") return;
    if (upcomingAdherents.length > 0) return;

    let alive = true;
    (async () => {
      setLoadingUpcoming(true);
      try {
        const data = await getUpcomingBirthdays();
        if (!alive) return;
        setUpcomingAdherents(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        if (alive) {
          setUpcomingAdherents([]);
          toast.error("Erreur lors du chargement des anniversaires à venir.");
        }
      } finally {
        if (alive) setLoadingUpcoming(false);
      }
    })();

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const list = mode === "today" ? birthdayAdherents : upcomingAdherents;
  const hasBirthdays = list.length > 0;

  const withSending = (id, on = true) =>
    setSendingIds((prev) => {
      const next = new Set(prev);
      on ? next.add(id) : next.delete(id);
      return next;
    });

  const resolveUserIds = async (adherentId) => {
    if (userIdsCache[adherentId]) return userIdsCache[adherentId];
    try {
      const users = await getUserIdByAdherentId(adherentId);
      const ids = (Array.isArray(users) ? users : []).map(pickUserId).filter(Boolean);
      const unique = Array.from(new Set(ids));
      setUserIdsCache((prev) => ({ ...prev, [adherentId]: unique }));
      return unique;
    } catch {
      return [];
    }
  };

  // ✅ helpers date pour le mode "next30"
  const startOfDay = (d) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const isLeap = (y) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;

  const nextBirthdayOccurrence = (birthDate, fromDate = new Date()) => {
    if (!birthDate) return null;
    const b = new Date(birthDate);
    if (Number.isNaN(b.getTime())) return null;

    const from = startOfDay(fromDate);
    const year = from.getFullYear();

    let occ = new Date(year, b.getMonth(), b.getDate());

    // 29/02 -> 28/02 si pas bissextile
    if (b.getMonth() === 1 && b.getDate() === 29 && !isLeap(year)) {
      occ = new Date(year, 1, 28);
    }

    occ = startOfDay(occ);

    if (occ < from) {
      const y2 = year + 1;
      occ = new Date(y2, b.getMonth(), b.getDate());
      if (b.getMonth() === 1 && b.getDate() === 29 && !isLeap(y2)) {
        occ = new Date(y2, 1, 28);
      }
      occ = startOfDay(occ);
    }
    return occ;
  };

  const inDaysFromToday = (occ) => {
    if (!occ) return null;
    const msDay = 24 * 60 * 60 * 1000;
    return Math.round((startOfDay(occ).getTime() - startOfDay(new Date()).getTime()) / msDay);
  };

  const dateShort = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("fr-FR", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
    });
  };

  const defaultMessage = (a) => {
    if (mode === "today") {
      return `Nous souhaitons un joyeux anniversaire à ${a?.prenom ?? ""} 🎂`;
    }
    const occ = nextBirthdayOccurrence(a?.date_naissance);
    const label = occ ? occ.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }) : "";
    return `Anniversaire à venir : ${a?.prenom ?? ""}${label ? ` le ${label}` : ""} 🎂`;
  };

  const handleSendOne = async (adherent) => {
    if (!hasToken) {
      toast.error("Vous devez être connecté pour envoyer la notification.");
      return;
    }
    const id = adherent?.id;
    if (!id) return;

    withSending(id, true);
    try {
      const ids = await resolveUserIds(id);
      if (ids.length === 0) {
        toast.info("Aucun destinataire lié à cet adhérent.");
        return;
      }
      const tasks = ids.map((uid) => sendNotification(defaultMessage(adherent), uid));
      const results = await Promise.allSettled(tasks);
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const ko = results.length - ok;
      if (ok && !ko) toast.success("Notification envoyée ✅");
      else if (ok && ko) toast.warn(`Partiel : ${ok} envoyée(s), ${ko} échec(s)`);
      else toast.error("Échec d’envoi de la notification.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l’envoi de la notification.");
    } finally {
      withSending(id, false);
    }
  };

  const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
    if (!s.startsWith("/uploads")) return s;
    const base = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
    return `${base}${s}`;
  };

  return (
    <section className="my-4 rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
      <div className="px-4 sm:px-6 py-2.5 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 text-white flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <FaBirthdayCake className="text-xl shrink-0" />

          <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xl font-semibold bg-black text-white shrink-0">
            {list.length}
          </span>

          <h3 className="text-sm sm:text-base font-semibold truncate">
            {mode === "today"
              ? `Anniversaire(s) : ${todayLabel}`
              : "Prochains anniversaires (30 jours)"}
          </h3>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* ✅ Toggle mode */}
          <button
            type="button"
            onClick={() => {
              setMode((m) => (m === "today" ? "next30" : "today"));
              setExpanded(true);
            }}
            className="rounded-full bg-white/90 text-slate-800 hover:bg-white px-3 h-8 text-xs font-bold"
            title={mode === "today" ? "Voir les 30 prochains jours" : "Revenir à aujourd’hui"}
          >
            {mode === "today" ? "30 jours" : "Aujourd’hui"}
          </button>

          {/* Expand / collapse */}
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
            className="rounded-full bg-white/90 text-slate-800 hover:bg-white w-8 h-8 min-h-0 leading-none"
            title={expanded ? "Replier" : "Déployer"}
          >
            {expanded ? "▲" : "▼"}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="p-4">
          {mode === "next30" && loadingUpcoming ? (
            <div className="text-center text-slate-600 dark:text-slate-300 py-2">
              Chargement des anniversaires à venir…
            </div>
          ) : !hasBirthdays ? (
            <div className="text-center text-slate-600 dark:text-slate-300 py-2">
              {mode === "today"
                ? "Aucun anniversaire aujourd’hui."
                : "Aucun anniversaire dans les 30 prochains jours."}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {list.map((a) => {
                const sending = sendingIds.has(a.id);

                const occ = mode === "next30" ? nextBirthdayOccurrence(a?.date_naissance) : null;
                const inDays = mode === "next30" ? inDaysFromToday(occ) : null;

                const ageNow = calculateAge?.(a.date_naissance);
                const ageDisplay =
                  mode === "today"
                    ? ageNow
                    : Number.isFinite(ageNow)
                      ? ageNow + (inDays && inDays > 0 ? 1 : 0)
                      : ageNow;

                const img =
                  toAbsolute(a?.photo_url) ||
                  getDefaultPhoto?.(a?.genre) ||
                  "/img/judoka_avatar.png";

                return (
                  <div
                    key={a.id}
                    className="relative rounded-xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-slate-200 dark:bg-slate-900"
                  >
                    <div className="p-3 flex flex-col items-center">
                      <img
                        src={img}
                        alt={`${a?.nom ?? ""} ${a?.prenom ?? ""}`}
                        className="w-20 h-20 rounded-full object-cover mb-2 ring-2 ring-white/80"
                        onError={(e) => {
                          e.currentTarget.src = getDefaultPhoto?.(a?.genre) || "/img/judoka_avatar.png";
                        }}
                      />

                      <p className="text-sm font-semibold text-center">{a?.nom}</p>
                      <p className="text-sm text-center -mt-0.5">{a?.prenom}</p>

                      {/* ✅ badge date seulement en mode 30 jours */}
                      {mode === "next30" && occ ? (
                        <span className="mt-1 text-[11px] px-2 py-0.5 rounded-full bg-white/80 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                          {dateShort(occ)}
                          {Number.isFinite(inDays) ? ` • J+${inDays}` : ""}
                        </span>
                      ) : null}

                      <div className="mt-2 inline-flex items-center gap-2">
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                          {Number.isFinite(ageDisplay) ? `${ageDisplay} ans` : "—"}
                        </span>

                        <button
                          type="button"
                          aria-label="Envoyer une notification"
                          title={hasToken ? "Notifier" : "Connectez-vous pour notifier"}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSendOne(a);
                          }}
                          disabled={sending || !hasToken}
                          className={`shrink-0 rounded-full bg-primary text-white w-8 h-8 flex items-center justify-center shadow transition-transform hover:scale-105 ${
                            sending || !hasToken ? "opacity-70 pointer-events-none" : ""
                          }`}
                        >
                          {sending ? (
                            <span className="loading loading-spinner loading-xs" />
                          ) : (
                            <FaBell className="text-base" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default BirthdaySection;